import { useCallback, useState, useEffect } from "react"
import { useGlobalContext } from "../context/useGlobalContext";
import { getRestoByAdminId, getRestoByHostId, getCountHostByPlaceId, getCountryByPlaceId, getPositionRankingByPlaceId, getLikeByPlaceId, getNotifyByPlaceId } from "../services/restoService"
import { useUser } from "./useUser";
import { getTenantRestaurants } from "../services/tenantOwnerService";
import { getTenantId } from "../utils/tenant";
import { useOwnerRestaurantSelection } from "./useOwnerRestaurantSelection";

export const useResto = () => {
    const { resto, setResto, restoHostCount, setRestoHostCount, restoCountry, setRestoCountry, restoPosition, setRestoPosition, restoLikes, setRestoLikes, restoCountNotifys, setRestoCountNotifys, } = useGlobalContext()
    const [isCoke, setIsCoke] = useState(false);
    const [isHuari, setIsHuari] = useState(false);
    const { getAvatarUser, isHost, isResto, isCEO, isOwner } = useUser()
    const { selectedOwnerRestaurantId, selectedOwnerRestaurant } = useOwnerRestaurantSelection();

    const normalizeCountry = useCallback((country) => {
        return String(country || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .trim();
    }, []);

    const getOwnerStatsRestoId = useCallback(() => {
        if (!isOwner) return undefined;
        return String(
            selectedOwnerRestaurantId ||
            selectedOwnerRestaurant?._id ||
            selectedOwnerRestaurant?.id ||
            resto?._id ||
            resto?.id ||
            ""
        ) || undefined;
    }, [isOwner, selectedOwnerRestaurantId, selectedOwnerRestaurant, resto]);

    const selectIsPromotionByCountry = useCallback((country) => {
        // Primero resetear ambas marcas
        setIsCoke(false);
        setIsHuari(false);

        // Luego establecer la marca correcta basado en el país
        const countryLower = normalizeCountry(country);
        switch (countryLower) {
            case "colombia":
            case "co":
                setIsCoke(true);
                break;
            case "bolivia":
            case "bo":
                setIsHuari(true);
                break;
            case "peru":
            case "pe":
                setIsCoke(true);
                break;
            default:
                break;
        }
    }, [normalizeCountry])

    // Actualizar marca visible cuando cambie el resto
    useEffect(() => {
        if (resto?.country) {
            selectIsPromotionByCountry(resto.country);
        }
    }, [resto?.country, selectIsPromotionByCountry])

    const getByAdminId = useCallback(async () => {
        const res = await getRestoByAdminId();
        setResto(res.data);
        selectIsPromotionByCountry(res?.data?.country);
        getAvatarUser(res.data?.brand, res.data?.name || "");
        return res;
    }, [setResto, getAvatarUser, selectIsPromotionByCountry])

    const getByHostId = useCallback(async () => {
        const res = await getRestoByHostId();
        setResto(res?.data);
        selectIsPromotionByCountry(res?.data?.country);
        return res;
    }, [setResto, selectIsPromotionByCountry])

    const getByOwnerTenant = useCallback(async () => {
        const tenantId = getTenantId();
        if (!tenantId) return null;

        const res = await getTenantRestaurants(tenantId);
        const list = Array.isArray(res)
            ? res
            : Array.isArray(res?.data)
                ? res.data
                : [];
        const firstResto = list[0] || null;
        if (firstResto) {
            setResto(firstResto);
            selectIsPromotionByCountry(firstResto?.country);
            getAvatarUser(firstResto?.brand, firstResto?.name || "");
        }
        return firstResto;
    }, [setResto, selectIsPromotionByCountry, getAvatarUser])

    const getResto = useCallback(() => {
        if (isOwner) {
            return getByOwnerTenant();
        } else if (isResto && !isCEO) {
            return getByAdminId();
        } else if (isHost) {
            return getByHostId();
        }
        return null;
    }, [isOwner, isResto, isCEO, isHost, getByOwnerTenant, getByAdminId, getByHostId])


    const getPosition = useCallback(async () => {
        try {
            const res = await getPositionRankingByPlaceId(getOwnerStatsRestoId());
            if (res && typeof res === 'object' && !res.error) {
                setRestoPosition(res);
            }
            return res;
        } catch (error) {
            console.error('Error en getPosition:', error);
            return null;
        }
    }, [getOwnerStatsRestoId, setRestoPosition])

    const getCountHost = useCallback(async () => {
        try {
            const res = await getCountHostByPlaceId(getOwnerStatsRestoId());
            // Asegurar que siempre sea un número
            if (typeof res === 'number') {
                setRestoHostCount(res);
            } else if (res !== null && res !== undefined) {
                const num = Number(res);
                if (!isNaN(num)) {
                    setRestoHostCount(num);
                } else {
                    setRestoHostCount(0);
                }
            } else {
                setRestoHostCount(0);
            }
            return res;
        } catch (error) {
            console.error('Error en getCountHost:', error);
            setRestoHostCount(0);
            return null;
        }
    }, [getOwnerStatsRestoId, setRestoHostCount])

    const getNotifyCount = useCallback(async () => {
        try {
            const res = await getNotifyByPlaceId(getOwnerStatsRestoId());
            // Asegurar que siempre sea un número
            if (typeof res === 'number') {
                setRestoCountNotifys(res);
            } else if (res !== null && res !== undefined) {
                const num = Number(res);
                if (!isNaN(num)) {
                    setRestoCountNotifys(num);
                } else {
                    setRestoCountNotifys(0);
                }
            } else {
                setRestoCountNotifys(0);
            }
            return res;
        } catch (error) {
            console.error('Error en getNotifyCount:', error);
            setRestoCountNotifys(0);
            return null;
        }
    }, [getOwnerStatsRestoId, setRestoCountNotifys])

    const getLikesCount = useCallback(async () => {
        try {
            const res = await getLikeByPlaceId(getOwnerStatsRestoId());
            // Asegurar que siempre sea un número
            if (typeof res === 'number') {
                setRestoLikes(res);
            } else if (res !== null && res !== undefined) {
                const num = Number(res);
                if (!isNaN(num)) {
                    setRestoLikes(num);
                } else {
                    setRestoLikes(0);
                }
            } else {
                setRestoLikes(0);
            }
            return res;
        } catch (error) {
            console.error('Error en getLikesCount:', error);
            setRestoLikes(0);
            return null;
        }
    }, [getOwnerStatsRestoId, setRestoLikes])

    const getCountryResto = useCallback(async () => {
        try {
            const res = await getCountryByPlaceId(getOwnerStatsRestoId());
            // Asegurar que sea un objeto válido con country
            if (res && typeof res === 'object' && !Array.isArray(res) && res.country !== undefined) {
                setRestoCountry({ country: String(res.country || '') });
            } else {
                setRestoCountry({ country: '' });
            }
            return res;
        } catch (error) {
            console.error('Error en getCountryResto:', error);
            setRestoCountry({ country: '' });
            return null;
        }
    }, [getOwnerStatsRestoId, setRestoCountry])

    return {
        resto,
        restoPosition,
        restoHostCount,
        restoCountry,
        restoLikes,
        restoCountNotifys,
        isCoke,
        isHuari,
        getResto,
        getPosition,
        getCountHost,
        getNotifyCount,
        getCountryResto,
        getLikesCount
    }
}
